import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import { ReactElement, useEffect, useState } from 'react';
import SendIcon from '@mui/icons-material/Send';
import EmptyPlaceholder from 'shared/components/EmptyPlaceholder';
import { AppPrimaryButtonOutlined } from 'shared/components/AppComponents';
import { ManageEmployeesService } from 'services/ManageEmployeesService';
import { Toast, Utils } from 'shared/helpers';
import { IInvitation } from './types';
import { ManageTeamService } from 'services/ManageTeamService';
import { useDispatch, useSelector } from 'react-redux';
import { IAppState } from 'state/RootReducer';
import { PaginationActions } from 'state/pagination/Action';

export const formatRoleName = (value: string): string =>
  value === 'GovernanceReviewer' ? 'Governance Reviewer' : value;

const PendingInvitesTab = (): ReactElement => {
  const dispatch = useDispatch();
  const invitationPagination = useSelector(
    (state: IAppState) => state.paginationState.all_invitation
  );

  const [resendId, setResendId] = useState('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [invitations, setInvitations] = useState<IInvitation[]>([]);

  // API Calls
  const loadInvitations = (): void => {
    setIsLoading(true);
    const queryString = new URLSearchParams({
      offset: String(invitationPagination.offset),
      limit: String(invitationPagination.pageLimit),
      search: invitationPagination.search,
    }).toString();
    ManageTeamService.getAll(queryString)
      .then((response) => {
        const { invite, pagination } = response.data.data;
        setInvitations(invite);

        dispatch(
          PaginationActions.setPagination({
            all_invitation: {
              offset: pagination.offset,
              pageLimit: pagination.limit,
              totalRecords: pagination.total,
              totalPages: pagination.totalPages,
            },
          })
        );
      })
      .catch((error: any) => {
        Toast.showInfoMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const actionOnResend = (row: IInvitation): void => {
    setResendId(row._id);
    ManageEmployeesService.resendInvite(row._id)
      .then(() => {
        Toast.showSuccessMessage('Invitation sent successfully');
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        setResendId('');
      });
  };

  // Instance Methods
  const handlePageChange = (_: unknown, page: number): void => {
    dispatch(
      PaginationActions.setPagination({
        all_invitation: {
          offset: page + 1,
        },
      })
    );
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const pageLimit = Number(event.target.value);

    dispatch(
      PaginationActions.setPagination({
        all_invitation: {
          offset: 1,
          pageLimit,
        },
      })
    );
  };

  // Hooks
  useEffect(() => {
    loadInvitations();
  }, [
    invitationPagination.offset,
    invitationPagination.pageLimit,
    invitationPagination.search,
  ]);

  // UI Elements
  return (
    <Box className="app-plain-card">
      <Box className="content">
        {isLoading && (
          <Box className="empty-content">
            <CircularProgress />
          </Box>
        )}
        {!isLoading && invitations.length === 0 && (
          <Box className="empty-content">
            <EmptyPlaceholder title="No record found" />
          </Box>
        )}
        {!isLoading && invitations.length > 0 && (
          <TableContainer>
            <Table className="app-table">
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invitations.map((row) => (
                  <TableRow key={row._id}>
                    <TableCell>{row.receiverEmail}</TableCell>
                    <TableCell>{formatRoleName(row.role)}</TableCell>
                    <TableCell>
                      <Box
                        style={{
                          display: 'flex',
                          columnGap: '10px',
                        }}
                      >
                        <AppPrimaryButtonOutlined
                          isLoading={resendId === row._id}
                          onClick={() => {
                            actionOnResend(row);
                          }}
                          loadingText="Resending"
                          title="Resend"
                          className="btn-small"
                          startIcon={<SendIcon />}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!isLoading && invitations.length > 0 && (
          <TablePagination
            component="div"
            count={invitationPagination.totalRecords}
            page={invitationPagination.offset - 1}
            rowsPerPage={invitationPagination.pageLimit}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 20, 50]}
            disabled={isLoading}
          />
        )}
      </Box>
    </Box>
  );
};

export default PendingInvitesTab;
